import { useEffect, useRef, useState } from "react";
import type { CSSProperties, DragEvent, MouseEvent, ReactNode } from "react";
import { useT } from "../i18n/hooks.ts";
import { CanvasRectangleSelect, type CanvasSelectionMode } from "./CanvasRectangleSelect.tsx";
import {
  groupBasinsByAquifer,
  groupBasinsByOutlet,
  groupBasinsBySnowZone,
  groupBasinsByZone,
} from "../model/grouping.ts";
import { buildProblemsFor } from "../model/subBasinState.ts";
import type { SubBasinLayer } from "../model/gisLayer.ts";
import { aquiferName, outletPointName, modelTypeName, zoneName } from "../model/entityNames.ts";
import {
  assignOutletAquiferInBulk,
  assignSurfaceOutletInBulk,
  assignZoneInBulk,
} from "../model/projectEntities.ts";
import type { LoadedProject } from "../model/project.ts";
import type { DailySeries } from "../model/dailySeries.ts";
import type { AemetProvenance } from "../model/storage.ts";
import type { SubBasin, EvalHidProject } from "../model/types.ts";
import type { PanelKind } from "./editor/panelDefinitions.ts";
import { AssignmentColorLegend } from "./AssignmentColorLegend.tsx";
import {
  buildColorMap,
  colorFor,
  textColorFor,
  type AssignmentColorMode,
} from "../model/assignmentColors.ts";
import { InfoIcon } from "./InfoIcon.tsx";
import { SnowIcon } from "./SnowIcon.tsx";
import type { MapSelectionMode, MapSeriesMode } from "./mapModes.ts";
import { EntityEditDialogs } from "./EntityEditDialogs.tsx";
import { useEntityEditDialogs } from "./useEntityEditDialogs.ts";

type CanvasTab = "zones" | "snow" | "outlets" | "aquifers";

function chipClass(hasProblems: boolean, selected: boolean): string {
  const clases = ["canvas-chip"];
  if (hasProblems) clases.push("canvas-chip-issue");
  if (selected) clases.push("canvas-chip-selected");
  return clases.join(" ");
}

/** Same lines as the map popup (contenidoPopup, SubBasinMap.tsx) — see Néstor's request that both views show the same info. Reuses the same "mapa.*" keys on purpose. */
function subBasinTitle(
  t: ReturnType<typeof useT>,
  project: EvalHidProject,
  c: SubBasin,
  problems: string[]
): string {
  const lines = [
    c.description,
    t("map.area", { km2: c.area.toFixed(1) }),
    t("map.zone", {
      zone: zoneName(t, project, c.zoneCode),
      model: modelTypeName(t, project, c.modelTypeCode, 0),
    }),
  ];
  if (c.snowZoneCode != null) {
    lines.push(
      t("map.snowZone", {
        zone: zoneName(t, project, c.snowZoneCode),
        model: modelTypeName(t, project, c.snowModelTypeCode, 1),
      })
    );
  }
  lines.push(t("map.outletColon", { name: outletPointName(t, project, c.outletCode) }));
  if (c.aquiferCode !== 0) {
    lines.push(t("map.aquiferColon", { name: aquiferName(project, c.aquiferCode) }));
  }
  lines.push(...problems.map((m) => `⚠ ${m}`));
  return lines.join("\n");
}

/**
 * Alternative to the map: sub-basins grouped into 4 tabs (zone, snow zone,
 * outlet point, aquifer) as cards instead of polygons — one tab at a time,
 * each with an always-present "unassigned" box on the left (a real drop
 * target: drag a sub-basin there to unassign it) plus one box per defined
 * entity of that kind. It used to only be used when the project had no GIS
 * layer — now it can also be chosen by hand (see the toggle in TopBar/
 * App.tsx) even with GIS available, so editing mode works the same in both
 * cases: here a card acts as the "polygon" (clickable/selectable), in
 * SubBasinMap.tsx the polygon itself is.
 *
 * Outlet points and aquifers have no geometry of their own in any case (see
 * projectEntities.ts) — their own tabs here are the only visual surface (besides
 * "Edit project"'s own catalogs) showing which sub-basins point to which.
 */
export function SubBasinCanvas({
  project,
  series,
  gisLayer,
  selectionMode,
  seriesMode,
  editorPanel,
  onChangeProject,
  onChangeSeries,
  aemetProvenance,
  onRecordAemetProvenance,
  observedFlow,
  onChangeObservedFlow,
  calibrationMode,
}: {
  project: EvalHidProject;
  series: LoadedProject["series"];
  /** null: the project has no linked GIS layer. Present: viewing as cards was chosen (see the TopBar toggle), not because GIS is missing — the "no GIS layer" notice doesn't apply in that case. */
  gisLayer?: SubBasinLayer | null;
  selectionMode?: MapSelectionMode;
  /** Choosing which sub-basin/aquifer to filter the "Input series" charts by — see SeriesPanel.tsx/SubBasinMap.tsx. */
  seriesMode?: MapSeriesMode;
  /** Active tab of "Edit project" (EditBar.tsx) — on change, closes any individual edit dialog opened from here, see the useEffect below. */
  editorPanel?: PanelKind;
  onChangeProject?: (project: EvalHidProject) => void;
  onChangeSeries?: (series: LoadedProject["series"]) => void;
  /** Which AEMET station(s) supplied each sub-basin's series so far — see AemetFetchDialog.tsx. */
  aemetProvenance?: Record<number, AemetProvenance>;
  onRecordAemetProvenance?: (subBasinCode: number, provenance: AemetProvenance) => void;
  /** Observed daily flow, one column per outlet point name — see storage.ts's own comment, edited from EditOutletPointDialog's "Observado" tab. */
  observedFlow?: DailySeries | null;
  onChangeObservedFlow?: (series: DailySeries | null) => void;
  /** CalibrationPanel.tsx open — the real "modo calibración" flag EditOutletPointDialog's "Serie observada" tab gates on (see App.tsx's State.calibrationPanelOpen). */
  calibrationMode?: boolean;
}) {
  const t = useT();
  const [activeTab, setActiveTab] = useState<CanvasTab>("zones");
  const {
    setEditingSubBasin,
    editingSubBasinObj,
    setEditingOutlet,
    editingOutletObj,
    setEditingAquifer,
    editingAquiferObj,
  } = useEntityEditDialogs(project);

  // An individual edit dialog (sub-basin/outlet point/aquifer) opened from
  // here is independent of "Edit project" (EditBar), which lives elsewhere
  // in the tree and doesn't unmount it on tab change — without this, the
  // dialog could be left open over a completely different tab (e.g. editing
  // a sub-basin while looking at "Input series"), confusing and unrelated
  // to what's shown underneath.
  useEffect(() => {
    setEditingSubBasin(null);
    setEditingOutlet(null);
    setEditingAquifer(null);
  }, [editorPanel, setEditingSubBasin, setEditingOutlet, setEditingAquifer]);
  const [dragging, setDragging] = useState<Set<number> | null>(null);
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  /**
   * Multi-selection while NEITHER selectionMode nor seriesMode is active
   * (i.e. "Edit project" is closed) — lets several cards be shift/ctrl
   * (drag-)selected and then dragged together onto a zone/outlet/aquifer
   * card, same as selectionMode's own set does when the editor IS open (see
   * toggleChip/startDrag below), without requiring the editor panel just
   * for that. Own component-local state, not lifted to App.tsx, because
   * outside the editor there's nothing else that needs to read it (no
   * checkbox list, no bulk-delete button — just this component's own drag
   * source). Cleared whenever an external mode takes over the selection
   * meaning (below), so a stale local selection doesn't silently reappear
   * highlighted once the editor closes again.
   */
  const [localSelection, setLocalSelection] = useState<Set<number>>(new Set());
  const hasExternalSelection = !!selectionMode || !!seriesMode;
  useEffect(() => {
    if (hasExternalSelection) setLocalSelection(new Set());
  }, [hasExternalSelection]);
  /**
   * Which group box (any tab — "zone:5", "zone:unassigned", "outlet:3"...)
   * a drag is currently over, for the drop-target highlight. Native
   * dragenter/dragleave fire for every child element the mouse crosses, not
   * just on entering/leaving the whole box: a group box has children (the
   * `<h4>`, the chips) that trigger a dragleave of the container followed by
   * a dragenter — without this counter, the highlight flickers (disappears
   * for an instant) every time the mouse passes over one of those children
   * instead of staying fixed while still over the box. Counted per group
   * instead of with a simple boolean because the mouse can enter one box
   * before finishing leaving the previous one.
   */
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const groupDragCounterRef = useRef(new Map<string, number>());
  /** Boxes the user collapsed by clicking their title — keyed the same as `hoveredGroup` (e.g. "zone:5"), so state survives switching tabs and back. */
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  function toggleCollapsed(groupKey: string) {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }

  function enterGroup(key: string) {
    const counter = groupDragCounterRef.current;
    counter.set(key, (counter.get(key) ?? 0) + 1);
    setHoveredGroup(key);
  }

  function leaveGroup(key: string) {
    const counter = groupDragCounterRef.current;
    const remaining = (counter.get(key) ?? 1) - 1;
    if (remaining <= 0) {
      counter.delete(key);
      setHoveredGroup((current) => (current === key ? null : current));
    } else {
      counter.set(key, remaining);
    }
  }

  const zoneGroups = groupBasinsByZone(project.subBasins, project.zones);
  // Independent of `zoneGroups`: a sub-basin can have both a runoff zone and
  // a snow zone at once, so it can appear in both.
  const snowGroups = groupBasinsBySnowZone(project.subBasins, project.zones);
  const outletGroups = groupBasinsByOutlet(project.subBasins, project.outletPoints);
  const aquiferGroups = groupBasinsByAquifer(project.subBasins, project.aquifers);
  const problemsOf = buildProblemsFor(t, series, project);

  /**
   * Color-codes each chip by whichever entity it's assigned to on the
   * ACTIVE tab (assignmentColors.ts) — computed from this component's own
   * `activeTab`, not from a `colorMode` prop like SubBasinMap.tsx: unlike
   * the map, this canvas has its own tab bar (just above) that can be on a
   * different catalog than whatever EditBar.tsx happens to have open (e.g.
   * EditBar showing "Sub-basins" while this view is showing "Snow zones"),
   * so the map's App.tsx-derived colorMode wouldn't necessarily match what
   * these tabs are actually displaying. Entities come from the same
   * `*Groups` arrays used to build the boxes below, so a color's order here
   * always matches the boxes' own left-to-right order.
   */
  const colorMode: AssignmentColorMode =
    activeTab === "zones"
      ? {
          field: "zoneCode",
          entities: zoneGroups
            .filter((g) => g.zone != null)
            .map((g) => ({ code: g.zone!.zoneCode, name: g.zone!.name })),
        }
      : activeTab === "snow"
        ? {
            field: "snowZoneCode",
            entities: snowGroups
              .filter((g) => g.zone != null)
              .map((g) => ({ code: g.zone!.zoneCode, name: g.zone!.name })),
          }
        : activeTab === "outlets"
          ? {
              field: "outletCode",
              entities: outletGroups
                .filter((g) => g.entity != null)
                .map((g) => ({ code: g.entity!.outletCode, name: g.entity!.name })),
            }
          : {
              field: "aquiferCode",
              entities: aquiferGroups
                .filter((g) => g.entity != null)
                .map((g) => ({ code: g.entity!.aquiferCode, name: g.entity!.name })),
            };
  const colorMap = buildColorMap(colorMode.entities);

  type GroupBoxProps = Parameters<typeof renderGroupBox>[0];

  // One entry per box in the active tab, unassigned always first (guaranteed
  // by grouping.ts, see its own tests) — split below into the "unassigned"
  // left column and the stacked "assigned" right column.
  const activeGroupBoxes: GroupBoxProps[] =
    activeTab === "zones"
      ? zoneGroups.map((g) => ({
          groupKey: `zone:${g.zone?.zoneCode ?? "unassigned"}`,
          title: g.zone?.name ?? t("canvas.noZoneAssigned"),
          unassigned: g.zone == null,
          basins: g.basins,
          onDrop: () => dropOnZone("zoneCode", g.zone?.zoneCode ?? null),
        }))
      : activeTab === "snow"
        ? snowGroups.map((g) => ({
            groupKey: `snow:${g.zone?.zoneCode ?? "unassigned"}`,
            title: g.zone?.name ?? t("canvas.noSnowZoneAssigned"),
            icon: <SnowIcon />,
            unassigned: g.zone == null,
            basins: g.basins,
            onDrop: () => dropOnZone("snowZoneCode", g.zone?.zoneCode ?? null),
          }))
        : activeTab === "outlets"
          ? outletGroups.map((g) => ({
              groupKey: `outlet:${g.entity?.outletCode ?? "unassigned"}`,
              title: g.entity?.name ?? t("canvas.noOutletAssigned"),
              unassigned: g.entity == null,
              basins: g.basins,
              onDrop: () => dropOnOutlet(g.entity?.outletCode ?? null),
              onEdit: g.entity ? () => setEditingOutlet(g.entity!.outletCode) : undefined,
            }))
          : aquiferGroups.map((g) => ({
              groupKey: `aquifer:${g.entity?.aquiferCode ?? "unassigned"}`,
              title: g.entity?.name ?? t("canvas.noAquiferAssigned"),
              unassigned: g.entity == null,
              basins: g.basins,
              onDrop: () => dropOnAquifer(g.entity?.aquiferCode ?? 0),
              onEdit: g.entity
                ? () =>
                    seriesMode
                      ? seriesMode.onSelect({ kind: "aquifer", id: g.entity!.aquiferCode })
                      : setEditingAquifer(g.entity!.aquiferCode)
                : undefined,
            }));
  const [unassignedBox, ...assignedBoxes] = activeGroupBoxes;

  // Dragging only makes sense if there's somewhere to apply the result —
  // and not while browsing "Input series" (seriesMode): toggleChip already
  // routes a plain click there to series-filtering instead of editing, but
  // dragging a chip was still live, letting a click meant only to inspect a
  // sub-basin's series accidentally reassign its zone/outlet/aquifer if the
  // mouse moved even slightly during the click.
  const canDrag = !!onChangeProject && !seriesMode;

  function toggleChip(c: SubBasin, e: MouseEvent) {
    if (selectionMode) {
      // Shift ADDS, Ctrl/Cmd REMOVES — same never-toggles semantics as the
      // map's own click handler (SubBasinMap.tsx) and as this same click
      // dragged into a rectangle (handleRectangleSelect below). A plain
      // click (no modifier) still toggles, for a quick single (de)select.
      const copy = new Set(selectionMode.selection);
      if (e.shiftKey) copy.add(c.subBasinCode);
      else if (e.ctrlKey || e.metaKey) copy.delete(c.subBasinCode);
      else if (copy.has(c.subBasinCode)) copy.delete(c.subBasinCode);
      else copy.add(c.subBasinCode);
      selectionMode.onCambiarSeleccion(copy);
    } else if (seriesMode) {
      seriesMode.onSelect({ kind: "subBasin", id: c.subBasinCode });
    } else if (e.shiftKey || e.ctrlKey || e.metaKey) {
      // Editor closed: only Shift/Ctrl+click build the local multi-selection
      // (see its own comment) — a plain click keeps opening the edit dialog
      // below, so ordinary single-card browsing isn't changed by this.
      const copy = new Set(localSelection);
      if (e.shiftKey) copy.add(c.subBasinCode);
      else copy.delete(c.subBasinCode);
      setLocalSelection(copy);
    } else {
      setEditingSubBasin(c.subBasinCode);
    }
  }

  function handleRectangleSelect(rect: DOMRect, mode: CanvasSelectionMode) {
    if (!canvasEl) return;
    const touched = new Set<number>();
    canvasEl.querySelectorAll<HTMLElement>("[data-subbasin-code]").forEach((el) => {
      const box = el.getBoundingClientRect();
      const intersects =
        box.left < rect.right &&
        box.right > rect.left &&
        box.top < rect.bottom &&
        box.bottom > rect.top;
      if (intersects) touched.add(Number(el.dataset.subbasinCode));
    });
    if (touched.size === 0) return;
    const base = selectionMode ? selectionMode.selection : localSelection;
    const next = new Set(base);
    for (const code of touched) {
      if (mode === "add") next.add(code);
      else next.delete(code);
    }
    if (selectionMode) selectionMode.onCambiarSeleccion(next);
    else setLocalSelection(next);
  }

  /**
   * Drag and drop: move several sub-basins from one box to another
   * (including the "unassigned" one, to clear that field), or assign them
   * an outlet point/aquifer by dropping them onto its box. If the one being
   * dragged is already part of the current selection (selectionMode's, or
   * localSelection's when the editor is closed — see its own comment —
   * several marked), the whole selection moves; otherwise only that
   * sub-basin — same criteria as a file manager.
   */
  function startDrag(e: DragEvent, c: SubBasin) {
    const activeSelection = selectionMode
      ? selectionMode.selection
      : !seriesMode
        ? localSelection
        : null;
    const inMultiSelection =
      activeSelection && activeSelection.size > 1 && activeSelection.has(c.subBasinCode);
    setDragging(inMultiSelection ? activeSelection : new Set([c.subBasinCode]));
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires some data to be set for the drag to start.
    e.dataTransfer.setData("text/plain", String(c.subBasinCode));
  }

  function endDrag() {
    setDragging(null);
    setHoveredGroup(null);
    groupDragCounterRef.current.clear();
  }

  /**
   * Dropping onto a zone box assigns that zone in the field matching its
   * type (runoff or snow) without touching the other field: dragging from a
   * runoff zone to a snow one (or vice versa) adds the second assignment
   * instead of replacing the first, because they're independent. If the
   * target zone is the same type as one the sub-basin already had, it does
   * replace it — assignZoneInBulk only writes one field at a time, so
   * that's the natural behavior without extra logic. `zoneCode: null` (the
   * "unassigned" box) clears it.
   */
  function dropOnZone(field: "zoneCode" | "snowZoneCode", zoneCode: number | null) {
    if (dragging && onChangeProject) {
      onChangeProject(assignZoneInBulk(project, dragging, field, zoneCode));
    }
    endDrag();
  }

  /** `outletCode: null` (the "unassigned" box) clears it — same sentinel `SubBasin.outletCode` already uses. */
  function dropOnOutlet(outletCode: number | null) {
    if (dragging && onChangeProject) {
      onChangeProject(assignSurfaceOutletInBulk(project, dragging, outletCode));
    }
    endDrag();
  }

  /** `aquiferCode: 0` (the "unassigned" box) clears it — same sentinel `SubBasin.aquiferCode` already uses. */
  function dropOnAquifer(aquiferCode: number) {
    if (dragging && onChangeProject) {
      onChangeProject(assignOutletAquiferInBulk(project, dragging, aquiferCode));
    }
    endDrag();
  }

  function renderSubBasinChip(c: SubBasin) {
    const problems = problemsOf(c);
    const selected =
      !!selectionMode?.selection.has(c.subBasinCode) ||
      (seriesMode?.selection?.kind === "subBasin" && seriesMode.selection.id === c.subBasinCode) ||
      (!selectionMode && !seriesMode && localSelection.has(c.subBasinCode));
    const beingDragged = !!dragging?.has(c.subBasinCode);
    const assignColor = colorFor(colorMap, colorMode.field, c);
    return (
      <button
        key={c.subBasinCode}
        data-subbasin-code={c.subBasinCode}
        className={
          chipClass(problems.length > 0, selected) +
          " canvas-chip-assigned" +
          (beingDragged ? " canvas-chip-dragging" : "")
        }
        style={
          {
            "--chip-assign-color": assignColor,
            "--chip-assign-text": textColorFor(assignColor),
          } as CSSProperties
        }
        title={subBasinTitle(t, project, c, problems)}
        draggable={canDrag}
        onDragStart={(e) => startDrag(e, c)}
        onDragEnd={endDrag}
        onClick={(e) => toggleChip(c, e)}
      >
        {c.description}
      </button>
    );
  }

  /**
   * One box (zone/snow zone/outlet point/aquifer, or the always-present
   * "unassigned" one) — same shape for all 4 tabs. The title itself toggles
   * collapse/expand (useful now boxes stack vertically — see the layout
   * below); opening the entity's own edit dialog (outlet points/aquifers
   * only) moved to a separate pencil button so the two actions don't fight
   * over the same click target.
   */
  function renderGroupBox({
    groupKey,
    title,
    icon,
    unassigned,
    basins,
    onDrop,
    onEdit,
  }: {
    groupKey: string;
    title: string;
    icon?: ReactNode;
    unassigned: boolean;
    basins: SubBasin[];
    onDrop: () => void;
    onEdit?: () => void;
  }) {
    const collapsed = collapsedGroups.has(groupKey);
    return (
      <div
        key={groupKey}
        className={
          "canvas-group" +
          (unassigned ? " canvas-group-unassigned" : "") +
          (hoveredGroup === groupKey ? " canvas-group-drop-active" : "")
        }
        onDragOver={(e) => {
          if (dragging) e.preventDefault();
        }}
        onDragEnter={(e) => {
          if (!dragging) return;
          e.preventDefault();
          enterGroup(groupKey);
        }}
        onDragLeave={() => leaveGroup(groupKey)}
        onDrop={(e) => {
          e.preventDefault();
          onDrop();
        }}
      >
        <h4 className="canvas-group-header">
          <button
            type="button"
            className="canvas-group-title"
            onClick={() => toggleCollapsed(groupKey)}
            title={t(collapsed ? "canvas.expandGroup" : "canvas.collapseGroup")}
            aria-expanded={!collapsed}
          >
            <span className="canvas-group-chevron" aria-hidden="true">
              {collapsed ? "▸" : "▾"}
            </span>
            {icon}
            {icon && " "}
            {title}
          </button>
          {onEdit && (
            <button
              type="button"
              className="canvas-group-edit"
              onClick={onEdit}
              title={t("canvas.editEntity")}
            >
              ✎
            </button>
          )}
        </h4>
        {!collapsed && (
          <div className="canvas-chips">
            {basins.length === 0 && <p className="canvas-group-empty">{t("canvas.noSubBasins")}</p>}
            {basins.map(renderSubBasinChip)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="canvas" ref={setCanvasEl}>
      {(!gisLayer || !hasExternalSelection) && (
        <div className="canvas-header-info">
          {!gisLayer && <InfoIcon text={t("canvas.noGisLayer")} />}
          {/* Only while the editor is closed — with it open, SubBasinsPanel.tsx's own tab already explains the same gesture. */}
          {!hasExternalSelection && <InfoIcon text={t("canvas.multiSelectHelp")} />}
        </div>
      )}
      {project.subBasins.length === 0 ? (
        <p className="engine-notice">{t("canvas.noProjectSubBasins")}</p>
      ) : (
        <>
          <div className="edit-panel-tabs canvas-tabs">
            <button
              className={activeTab === "zones" ? "tab-active" : undefined}
              onClick={() => setActiveTab("zones")}
            >
              {t("panels.zones")}
            </button>
            <button
              className={activeTab === "snow" ? "tab-active" : undefined}
              onClick={() => setActiveTab("snow")}
            >
              <SnowIcon /> {t("canvas.snowZonesTab")}
            </button>
            <button
              className={activeTab === "outlets" ? "tab-active" : undefined}
              onClick={() => setActiveTab("outlets")}
            >
              {t("infoPopover.outletPoints")}
            </button>
            <button
              className={activeTab === "aquifers" ? "tab-active" : undefined}
              onClick={() => setActiveTab("aquifers")}
            >
              {t("infoPopover.aquifers")}
            </button>
          </div>

          <div className="canvas-groups-layout">
            {unassignedBox && (
              <div className="canvas-unassigned-col">{renderGroupBox(unassignedBox)}</div>
            )}
            <div className="canvas-assigned-col">{assignedBoxes.map(renderGroupBox)}</div>
          </div>
          <AssignmentColorLegend colorMode={colorMode} />
        </>
      )}

      <EntityEditDialogs
        project={project}
        series={series}
        gisLayer={gisLayer}
        aemetProvenance={aemetProvenance}
        onRecordAemetProvenance={onRecordAemetProvenance}
        onChangeProject={onChangeProject}
        onChangeSeries={onChangeSeries}
        editingSubBasinObj={editingSubBasinObj}
        editingOutletObj={editingOutletObj}
        editingAquiferObj={editingAquiferObj}
        onCloseSubBasin={() => setEditingSubBasin(null)}
        onCloseOutlet={() => setEditingOutlet(null)}
        onCloseAquifer={() => setEditingAquifer(null)}
        observedFlow={observedFlow}
        onChangeObservedFlow={onChangeObservedFlow}
        calibrationMode={calibrationMode}
      />

      {!seriesMode && <CanvasRectangleSelect onSelect={handleRectangleSelect} />}
      {!hasExternalSelection && localSelection.size > 0 && (
        <div className="canvas-selection-bar">
          <span>
            {t(localSelection.size > 1 ? "canvas.multipleSelected" : "canvas.oneSelected", {
              n: localSelection.size,
            })}
          </span>
          <button className="button" onClick={() => setLocalSelection(new Set())}>
            {t("subBasinsPanel.clearSelection")}
          </button>
        </div>
      )}
    </div>
  );
}
